import React, { useEffect, useMemo } from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import { Modifier } from 'react-day-picker/types/Modifiers';
import moment from 'moment';

interface DayPickerInputProps {
  handleFormChange?: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => void;
  handleStateChange?: (value: any) => void;
  value?: number;
  id: string;
  disabled: boolean;
  handleBlur?: (e: React.FocusEvent<any>) => void;
  onChange?: (e: React.ChangeEvent<any>) => void;
  disabledDays?: Modifier | Modifier[];
}

export default function DayPickerInputValue({
  handleFormChange,
  handleStateChange,
  onChange,
  handleBlur,
  id,
  value,
  disabledDays,
}: DayPickerInputProps): JSX.Element {
  const handleDayChange = (day: Date) => {
    handleFormChange?.(id, day, true) || handleStateChange?.(day);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialMonth = useMemo(() => {
    if (!disabledDays) return new Date();

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = today.getDate(); day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const disabled = Array.isArray(disabledDays)
        ? disabledDays.some((mod) => matchModifier(date, mod))
        : matchModifier(date, disabledDays);

      if (!disabled) return today;
    }

    return new Date(year, month + 1, 1);
  }, [disabledDays]);

  return (
    <DayPickerInput
      placeholder="DD/MM/YYYY"
      format="DD/MM/YYYY"
      formatDate={MomentLocaleUtils.formatDate}
      parseDate={MomentLocaleUtils.parseDate}
      onDayChange={handleDayChange}
      onChange={onChange}
      onBlur={handleBlur}
      value={value ? new Date(value) : ''}
      dayPickerProps={{
        disabledDays: disabledDays || [],
        fromMonth: new Date(),
        initialMonth,
      }}
    />
  );
}

const matchModifier = (date: Date, mod: Modifier): boolean => {
  if (!mod) return false;
  if (mod instanceof Date) return moment(mod).isSame(date, 'day');
  if (typeof mod === 'function') return mod(date);
  if (typeof mod === 'object') {
    const { before, after, from, to } = mod as any;
    return (
      (before && date < before) ||
      (after && date > after) ||
      (from && to && date >= from && date <= to) ||
      false
    );
  }
  return false;
};
